import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { Plus, Search, ShieldCheck } from 'lucide-react';
import { CERTIFICATE_STATUSES, type CertificateStatus } from '@geoic/shared';
import { Badge } from '@/components/ui/Badge.js';
import { Button } from '@/components/ui/Button.js';
import { Select } from '@/components/ui/Field.js';
import { dateOnly } from '@/lib/format.js';
import { cn } from '@/lib/cn.js';
import { queryKeys } from '@/lib/query-keys.js';
import { CertificateFormModal } from './CertificateFormModal.js';
import {
  CERTIFICATE_STATUS_LABELS,
  VERDICT_LABELS,
  certificatesApi,
  verdictOf,
  type CertificateListParams,
  type CertificateRecord,
} from './certificates-api.js';

/**
 * Sertifikatlar ro'yxati.
 *
 * Qidiruv raqam va F.I.Sh. bo'yicha — FAQAT shu yerda. Saytda bunday
 * qidiruv ataylab qilinmagan (docs/12-SERTIFIKAT-REJA.md §3.6).
 *
 * Filtr holati manzilda: xodim kartochkadan qaytganda ro'yxat joyida
 * qoladi (arizalar ekrani bilan bir xil xatti-harakat).
 */
export function CertificatesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [formOpen, setFormOpen] = useState(false);

  const status = searchParams.get('status') ?? '';
  const search = searchParams.get('search') ?? '';
  const page = Math.max(1, Number(searchParams.get('page') ?? 1) || 1);
  const [searchDraft, setSearchDraft] = useState(search);

  function patchParams(patch: Record<string, string>): void {
    const next = new URLSearchParams(searchParams);
    for (const [key, value] of Object.entries(patch)) {
      if (value) next.set(key, value);
      else next.delete(key);
    }
    if (!('page' in patch)) next.delete('page');
    setSearchParams(next);
  }

  const params = useMemo<CertificateListParams>(
    () => ({
      page,
      limit: 20,
      search: search.trim() || undefined,
      status: (status || undefined) as CertificateStatus | undefined,
    }),
    [page, search, status],
  );

  const list = useQuery({
    queryKey: queryKeys.certificates.list(params),
    queryFn: ({ signal }) => certificatesApi.list(params, signal),
  });

  const rows = list.data?.data ?? [];
  const total = list.data?.meta.total ?? 0;
  const totalPages = list.data?.meta.totalPages ?? 1;

  return (
    <div className="flex flex-col gap-[18px]">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <h1 className="text-[26px] font-semibold tracking-[-0.02em] text-n-900">Sertifikatlar</h1>
          <p className="mt-1.5 text-[13.5px] text-n-600">
            {list.isLoading ? 'Yuklanmoqda…' : `${total} ta sertifikat`}
          </p>
        </div>

        <Button icon={<Plus className="size-4" />} onClick={() => setFormOpen(true)}>
          Yangi sertifikat
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-2.5">
        <form
          onSubmit={(event) => {
            event.preventDefault();
            patchParams({ search: searchDraft.trim() });
          }}
          className="flex h-[38px] min-w-[240px] flex-1 items-center gap-2.5 border border-n-400 bg-white px-3 sm:max-w-[400px]"
        >
          <Search className="size-4 shrink-0 text-n-600" aria-hidden />
          <label className="sr-only" htmlFor="certificates-search">
            Qidirish
          </label>
          <input
            id="certificates-search"
            type="search"
            value={searchDraft}
            onChange={(event) => setSearchDraft(event.target.value)}
            placeholder="Seriya raqami yoki F.I.Sh."
            className="h-full min-w-0 flex-1 border-0 bg-transparent text-[13.5px] outline-none"
          />
        </form>

        <Select
          aria-label="Holat"
          value={status}
          placeholder="Barcha holatlar"
          options={CERTIFICATE_STATUSES.map((value) => ({
            value,
            label: CERTIFICATE_STATUS_LABELS[value],
          }))}
          onChange={(event) => patchParams({ status: event.target.value })}
          className="w-48"
        />

        {(status || search) && (
          <button
            type="button"
            onClick={() => {
              setSearchDraft('');
              setSearchParams(new URLSearchParams());
            }}
            className="h-[38px] px-3 text-[13.5px] text-brand-600 underline underline-offset-2"
          >
            Filtrni tozalash
          </button>
        )}
      </div>

      <div className="border border-n-200 bg-white">
        <div className="hidden overflow-x-auto lg:block">
          <table className="w-full border-collapse text-[13px]">
            <thead>
              <tr className="border-b border-n-900 bg-n-50 text-left">
                <th className="px-3.5 py-3 font-semibold whitespace-nowrap">Raqam</th>
                <th className="px-3 py-3 font-semibold">F.I.Sh.</th>
                <th className="px-3 py-3 font-semibold">Kurs</th>
                <th className="px-3 py-3 text-right font-semibold whitespace-nowrap">Soat</th>
                <th className="px-3 py-3 font-semibold whitespace-nowrap">Berilgan</th>
                <th className="px-3 py-3 font-semibold whitespace-nowrap">Amal qiladi</th>
                <th className="px-3.5 py-3 font-semibold whitespace-nowrap">Holat</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={row._id}
                  className={cn(
                    'border-b border-n-100 last:border-b-0 hover:bg-n-50',
                    // Bekor qilingani ro'yxatda darhol ajralib tursin
                    row.status === 'revoked' && 'bg-error-50',
                  )}
                >
                  <td className="px-3.5 py-3 whitespace-nowrap">
                    <Link
                      to={`/certificates/${row._id}`}
                      className="font-mono font-medium text-brand-600 hover:text-accent-600"
                    >
                      {row.number}
                    </Link>
                  </td>
                  <td className="px-3 py-3 font-medium text-n-900">{row.fullName}</td>
                  <td className="px-3 py-3 leading-snug text-n-600">{row.courseTitle}</td>
                  <td className="px-3 py-3 text-right font-mono tabular whitespace-nowrap">
                    {row.hours}
                  </td>
                  <td className="px-3 py-3 font-mono text-[12.5px] tabular whitespace-nowrap text-n-600">
                    {dateOnly(row.issuedAt)}
                  </td>
                  <td className="px-3 py-3 font-mono text-[12.5px] tabular whitespace-nowrap text-n-600">
                    {dateOnly(row.extendedUntil ?? row.expiresAt)}
                  </td>
                  <td className="px-3.5 py-3">
                    <VerdictBadge record={row} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <ul className="lg:hidden">
          {rows.map((row) => (
            <li key={row._id} className="border-b border-n-100 last:border-b-0">
              <Link to={`/certificates/${row._id}`} className="flex flex-col gap-2 p-4">
                <span className="flex w-full items-center gap-2.5">
                  <span className="font-mono text-[13.5px] font-semibold text-brand-600">
                    {row.number}
                  </span>
                  <VerdictBadge record={row} />
                </span>
                <span className="text-[15px] font-semibold text-n-900">{row.fullName}</span>
                <span className="text-[13.5px] leading-snug text-n-600">{row.courseTitle}</span>
                <span className="font-mono text-xs tabular text-n-600">
                  {dateOnly(row.issuedAt)} — {dateOnly(row.extendedUntil ?? row.expiresAt)}
                </span>
              </Link>
            </li>
          ))}
        </ul>

        {!list.isLoading && rows.length === 0 && (
          <div className="flex flex-col items-center gap-3 px-6 py-16 text-center">
            <ShieldCheck className="size-[34px] text-n-400" aria-hidden />
            <p className="text-lg font-semibold text-n-900">Sertifikat topilmadi</p>
            <p className="text-sm text-n-600">
              «Yangi sertifikat» tugmasi bilan birinchisini yarating.
            </p>
          </div>
        )}

        {list.isLoading && (
          <p className="px-6 py-16 text-center text-sm text-n-600">Yuklanmoqda…</p>
        )}
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between gap-4">
          <span className="text-[13px] text-n-600">
            {page}-sahifa, jami {totalPages}
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => patchParams({ page: String(page - 1) })}
            >
              Oldingi
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages}
              onClick={() => patchParams({ page: String(page + 1) })}
            >
              Keyingi
            </Button>
          </div>
        </div>
      )}

      <CertificateFormModal open={formOpen} onClose={() => setFormOpen(false)} />
    </div>
  );
}

export function VerdictBadge({ record }: { record: CertificateRecord }) {
  const verdict = verdictOf(record);
  const tone = verdict === 'valid' ? 'success' : verdict === 'expired' ? 'warning' : 'danger';
  return <Badge tone={tone}>{VERDICT_LABELS[verdict]}</Badge>;
}
